import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useSidebar } from '@/components/ui/sidebar';

/**
 * Types pour le contexte de layout
 */
export type LayoutType = 'default' | 'no-sidebar' | 'full-width';

export interface LayoutContextType {
  // Visibilité de la sidebar
  sidebarVisible: boolean;
  setSidebarVisible: (visible: boolean) => void;
  
  // Type de layout actuel
  currentLayout: LayoutType;
  setCurrentLayout: (layout: LayoutType) => void;
  
  // État de la sidebar (collapsed/expanded) - intégré avec SidebarProvider
  sidebarState: 'collapsed' | 'expanded';
  toggleSidebar: () => void;
  
  // Méthodes utilitaires
  showSidebar: () => void;
  hideSidebar: () => void;
  resetLayout: () => void;
}

/**
 * Contexte pour la gestion globale du layout
 */
const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

/**
 * Props pour le LayoutProvider
 */
interface LayoutProviderProps {
  children: ReactNode;
  defaultLayout?: LayoutType;
  defaultSidebarVisible?: boolean;
}

/**
 * LayoutProvider - Fournisseur de contexte pour la gestion du layout
 * 
 * Ce provider centralise la gestion de l'état du layout et s'intègre
 * avec le SidebarProvider existant pour assurer la cohérence.
 * 
 * Fonctionnalités:
 * - Gestion de la visibilité de la sidebar
 * - Gestion du type de layout (default, no-sidebar, full-width)
 * - Intégration avec le SidebarProvider existant
 * - Persistance de l'état lors des navigations
 * 
 * Exigences: 2.1, 2.2, 6.3
 */
export const LayoutProvider: React.FC<LayoutProviderProps> = ({
  children,
  defaultLayout = 'default',
  defaultSidebarVisible = true,
}) => {
  // État local du layout
  const [currentLayout, setCurrentLayout] = useState<LayoutType>(defaultLayout);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(defaultSidebarVisible);
  
  // Intégration avec le SidebarProvider existant
  const { state, toggleSidebar: sidebarToggle } = useSidebar();
  const sidebarState = state;
  
  // Synchronisation de la visibilité avec le type de layout
  useEffect(() => {
    if (currentLayout === 'no-sidebar' || currentLayout === 'full-width') {
      setSidebarVisible(false);
    } else {
      setSidebarVisible(defaultSidebarVisible);
    }
  }, [currentLayout, defaultSidebarVisible]);
  
  // Persistance de l'état dans le localStorage
  useEffect(() => {
    const savedLayout = localStorage.getItem('app-layout-type');
    const savedSidebarVisible = localStorage.getItem('app-sidebar-visible');
    
    if (savedLayout && ['default', 'no-sidebar', 'full-width'].includes(savedLayout)) {
      setCurrentLayout(savedLayout as LayoutType);
    }
    
    if (savedSidebarVisible !== null) {
      setSidebarVisible(savedSidebarVisible === 'true');
    }
  }, []);
  
  // Sauvegarde des changements dans le localStorage
  useEffect(() => {
    localStorage.setItem('app-layout-type', currentLayout);
    localStorage.setItem('app-sidebar-visible', sidebarVisible.toString());
  }, [currentLayout, sidebarVisible]);
  
  // Méthodes utilitaires
  const showSidebar = useCallback(() => {
    setSidebarVisible(true);
    if (currentLayout === 'no-sidebar') {
      setCurrentLayout('default');
    }
  }, [currentLayout]);
  
  const hideSidebar = useCallback(() => {
    setSidebarVisible(false);
  }, []);
  
  const resetLayout = useCallback(() => {
    setCurrentLayout('default');
    setSidebarVisible(true);
  }, []);
  
  // Wrapper pour toggleSidebar qui respecte la visibilité
  const toggleSidebar = useCallback(() => {
    if (sidebarVisible) {
      sidebarToggle();
    }
  }, [sidebarVisible, sidebarToggle]);
  
  // Valeur du contexte
  const contextValue: LayoutContextType = {
    sidebarVisible,
    setSidebarVisible,
    currentLayout,
    setCurrentLayout,
    sidebarState,
    toggleSidebar,
    showSidebar,
    hideSidebar,
    resetLayout,
  };
  
  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

/**
 * Hook pour utiliser le contexte de layout
 * 
 * @returns LayoutContextType - Contexte de layout avec toutes les méthodes
 * @throws Error si utilisé en dehors du LayoutProvider
 */
export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  
  return context;
};

export const useOptionalLayout = (): LayoutContextType | undefined => {
  return useContext(LayoutContext);
};

/**
 * Hook utilitaire pour vérifier si la sidebar doit être affichée
 * 
 * @returns boolean - true si la sidebar doit être visible
 */
export const useShouldShowSidebar = (): boolean => {
  const { sidebarVisible, currentLayout } = useLayout();
  return sidebarVisible && currentLayout !== 'no-sidebar' && currentLayout !== 'full-width';
};

/**
 * Hook utilitaire pour obtenir les classes CSS du layout
 * 
 * @returns string - Classes CSS pour le conteneur principal
 */
export const useLayoutClasses = (): string => {
  const { currentLayout, sidebarVisible, sidebarState } = useLayout();
  
  const baseClasses = 'min-h-screen w-full';
  
  if (!sidebarVisible || currentLayout === 'no-sidebar') {
    return `${baseClasses} layout-no-sidebar`;
  }
  
  if (currentLayout === 'full-width') {
    return `${baseClasses} layout-full-width`;
  }
  
  return `${baseClasses} layout-default layout-sidebar-${sidebarState}`;
};
